// Flow demand vertex schema
// Represents a fixed flow rate source or sink

import { z } from 'zod';
import { BaseVertexSchema } from '../base/vertex';
import { ValueWithUnitSchema } from '../base/common';

/**
 * Flow Demand Operation Mode Configurations
 * ADR-0006: Operating Mode Configuration Pattern
 * 
 * Different modes represent different flow directions (in vs out).
 * Using nested config to follow the same pattern as pumps and valves.
 */

// Mode: Flow In (source)
const FlowDemandInConfigSchema = z.object({
  mode: z.literal('flow_in'),
  value: z.number(),
  unit: z.string(), // e.g., "kg/s", "m³/s", "gpm"
});

// Mode: Flow Out (sink)
const FlowDemandOutConfigSchema = z.object({
  mode: z.literal('flow_out'),
  value: z.number(),
  unit: z.string(), // e.g., "kg/s", "m³/s", "gpm"
});

// Discriminated union of flow demand operation modes
export const FlowDemandOperationConfigSchema = z.discriminatedUnion('mode', [
  FlowDemandInConfigSchema,
  FlowDemandOutConfigSchema,
]);

export type FlowDemandOperationConfig = z.infer<typeof FlowDemandOperationConfigSchema>;

/**
 * Flow Demand Vertex Schema
 * Represents a fixed flow rate source or sink
 * 
 * Uses nested config pattern (ADR-0006) following the same structure as
 * pumps and valves for consistency.
 * 
 * @property elevation - Height of the flow demand point (for pressure calculations)
 */
export const FlowDemandVertexSchema = BaseVertexSchema.extend({
  type: z.literal('flow_demand'),
  
  // Calculation data
  elevation: ValueWithUnitSchema, // Height of the flow demand point
  operation: FlowDemandOperationConfigSchema, // Nested discriminated union
});

export type FlowDemandVertex = z.infer<typeof FlowDemandVertexSchema>;

